import { MoonIcon, SunIcon } from 'lucide-react';

import { Button } from '@splitfiesta/ui';

import { useTheme } from '@/hooks/useTheme';

import { useEffectiveTheme } from '@/stores/uiStore';

interface ThemeToggleProps {
  variant?: 'default' | 'auth';
  'data-testid'?: string;
}

export function ThemeToggle({
  variant = 'default',
  'data-testid': dataTestId,
}: ThemeToggleProps) {
  const { toggleTheme } = useTheme();
  const effectiveTheme = useEffectiveTheme();

  const iconContent = (
    <div className="relative w-6 h-6">
      <SunIcon
        className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${
          variant === 'auth' ? 'text-yellow-300' : 'text-yellow-500'
        } ${effectiveTheme === 'light' ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'}`}
      />
      <MoonIcon
        className={`absolute inset-0 w-6 h-6 text-blue-500 transition-all duration-300 ${
          effectiveTheme === 'dark'
            ? 'opacity-100 rotate-0'
            : 'opacity-0 -rotate-90'
        }`}
      />
    </div>
  );

  const ariaLabel =
    effectiveTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode';

  // Use plain button for default variant to avoid Button component's extra styling
  if (variant === 'default') {
    return (
      <button
        onClick={toggleTheme}
        className="relative rounded-full p-2 menu-btn"
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        {iconContent}
      </button>
    );
  }

  // Auth variant uses Button component
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleTheme}
      className="relative rounded-full px-3 py-2 border-2 bg-white/10 backdrop-blur-xs border-white/20 hover:bg-white/20 hover:border-orange-400 transition-all duration-200"
      data-testid={dataTestId}
      aria-label={ariaLabel}
    >
      {iconContent}
    </Button>
  );
}
